/* eslint-disable max-len */
import React from 'react';

const NemwelExperiences = () => (
  <div className="NemwelExperiences">
    {/* SUMMARY SECTION */}
    <div className="summary-section">
      <h2 className="section-header">Summary</h2>
      <p className="summary-text">
        Experienced software developer skilled in building adaptive web apps, performant APIs, and internal tools aligned with evolving business needs. Recognized for translating complex requirements into scalable Rails features and optimizing systems to deliver user-focused results in cross-functional team environments.
      </p>
    </div>

    {/* SKILLS SECTION */}
    <div className="skills-section">
      <h2 className="section-header">Technical Skills</h2>
      <div className="skills-grid">

        <div className="skill-group">
          <h3>Core Competencies</h3>
          <ul className="side-by-side">
            <li>Backend & API Architecture</li>
            <li>Test-Driven Development</li>
            <li>Continuous Integration</li>
            <li>Performance Optimization</li>
            <li>Debugging</li>
            <li>Data Integration</li>
            <li>Background Job Management</li>
            <li>Cross-functional Collaboration</li>
            <li>Asynchronous Communication</li>
            <li>Structured Documentation</li>
          </ul>
        </div>

        <div className="skill-group">
          <h3>Technical Stack</h3>
          <ul className="side-by-side">
            <li>Ruby / Ruby on Rails</li>
            <li>RSpec / Minitest</li>
            <li>Sidekiq</li>
            <li>Hotwire (Turbo/Stimulus)</li>
            <li>JavaScript / TypeScript</li>
            <li>React / NextJS</li>
            <li>PostgreSQL / Redis</li>
            <li>TailwindCSS</li>
            <li>HTML / CSS</li>
            <li>Capistrano</li>
          </ul>
        </div>

        <div className="skill-group full-width">
          <h3>Tools & Integrations</h3>
          <ul className="side-by-side">
            <li>Git / GitHub</li>
            <li>M-Pesa Daraja API</li>
            <li>REST API Integrations</li>
            <li>Ruby Scripting</li>
            <li>Python Automation</li>
            <li>AWS Cloud Practitioner</li>
            <li>Ubuntu / Linux</li>
            <li>Bash Scripting</li>
          </ul>
        </div>
      </div>
    </div>

    {/* EXPERIENCE SECTION */}
    <div className="experiences-section">
      <h2 className="section-header">Professional Experience</h2>
      <div className="timeline">

        {/* CRAFTSILICON */}
        <div className="experience-item">
          <div className="exp-dot" />
          <div className="exp-content">
            <h3 className="role">Mid-Level Ruby on Rails Developer</h3>
            <span className="duration">06/2025 – 12/2025</span>
            <h4 className="company">CRAFTSILICON – Nairobi, Kenya (ONSITE)</h4>
            <p className="role-summary">
              Led development of a Rails-based internal project management system for client and team use. Focused on building scalable, user-centered features that supported workflow automation, backend optimization, and cross-team alignment in a regulated, high-compliance technical environment.
            </p>
            <ul>
              <li>Engineered performant, user-friendly modules to improve system responsiveness, resulting in a 30% reduction in ticket resolution time.</li>
              <li>Optimized database queries by eliminating N+1 issues in critical workflows, leading to a 30% drop in server load and faster response times.</li>
              <li>Refactored frontend logic into modular Stimulus controllers, cutting inline script-related bugs by 50% and aligning with Hotwire best practices.</li>
              <li>Deployed updates to staging and production on Ubuntu using Capistrano; introduced Rubocop into CI to enforce consistency and reduce rework during peer reviews.</li>
              <li>Migrated over 500GB of data via Ruby scripts that pull, map, and store API data with 98% accuracy, ensuring system reliability during migration.</li>
              <li>Collaborated with Design, QA, and Project Management to clarify edge cases and define requirements, reducing unnecessary meetings and rework.</li>
              <li>Resolved client and internal support tickets, shortening turnaround time on critical issues and boosting user satisfaction.</li>
              <li>Facilitated training sessions to improve adoption and user engagement across internal teams.</li>
            </ul>
          </div>
        </div>

        {/* NOBILIS LTD */}
        <div className="experience-item">
          <div className="exp-dot" />
          <div className="exp-content">
            <h3 className="role">Software Developer – Ruby on Rails</h3>
            <span className="duration">07/2024 – 12/2024</span>
            <h4 className="company">NOBILIS LTD – Nairobi, Kenya (HYBRID)</h4>
            <p className="role-summary">
              Designed and built a Rails API backend powering a React app for a client in the transport sector in Kenya, delivering real-time driver location visibility to users through ActionCable websockets and improving operational transparency.
            </p>
            <ul>
              <li>Integrated M-Pesa Daraja into the payments module, enabling secure mobile transactions that increased successful payments, reduced support tickets by 20%, and strengthened cash flow reliability.</li>
              <li>Increased unit and integration test coverage by up to 70% increasing confidence in the system and tightening gaps in the implementation exposed by the written tests.</li>
              <li>Enhanced user experience and platform adoption by scaling the system from &lt;100 users to 1,000+ active users, supported by performance fixes and real-time broadcasting improvements.</li>
              <li>Worked closely with the frontend developer and the product owner to align API design, document endpoints with Rswag, and resolve integration blockers in an iterative environment.</li>
            </ul>
          </div>
        </div>

        {/* MICROVERSE SENIOR */}
        <div className="experience-item">
          <div className="exp-dot" />
          <div className="exp-content">
            <h3 className="role">Senior Code Reviewer</h3>
            <span className="duration">02/2023 – 01/2024</span>
            <h4 className="company">MICROVERSE INC – Remote</h4>
            <p className="role-summary">
              Promoted to a Senior role within 9 months based on technical depth, accuracy, and proactive support of team operations.
            </p>
            <ul>
              <li>Reviewed project pull requests for quality control, ensuring 90% adherence to style and best practices, reinforcing fairness and consistency.</li>
              <li>Delivered 1:1 technical guidance with empathy and clarity, enabling junior developers to improve submitted projects and gain confidence in debugging.</li>
              <li>Resolved peer disputes and clarified feedback disputes with professionalism, maintaining trust and a supportive learning environment.</li>
              <li>Mentored junior developers asynchronously across time zones via GitHub and written feedback, reducing repeated review cycles and improving team clarity.</li>
              <li>Supported peer reviewers proactively in the absence of senior staff, ensuring continuity in reviews and maintaining team efficiency.</li>
              <li>Enhanced curriculum repositories with targeted improvements, leading to a 25% increase in learner comprehension and engagement.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    {/* AWARDS & VOLUNTEERING */}
    <div className="extra-sections">
      <div className="section-block">
        <h2 className="section-header">Awards</h2>
        <div className="award-item">
          <h3>Highest Rated Code Reviewer – Microverse (11/2022)</h3>
          <p>Recognized for exceptional review quality and impactful feedback, contributing to improved coding standards among peers.</p>
        </div>
      </div>

      <div className="section-block">
        <h2 className="section-header">Memberships & Volunteering</h2>
        <div className="volunteer-item">
          <h3>Active Member & Volunteer, African Ruby Community (ARC)</h3>
          <ul>
            <li>Co-organized the 2024/25 Africa Ruby conferences, hosting over 500 attendees across four continents.</li>
            <li>Contribute as a technical writer and events organizer, documenting events and coordinating monthly meetups.</li>
          </ul>
        </div>
      </div>
    </div>

  </div>
);

export default NemwelExperiences;
